<template>
  <div class="flex flex-col items-center gap-5">
    <div
      class="flex gap-2.5 items-center justify-center"
      :class="{
        'animate-shake': hasError,
      }"
    >
      <button
        type="button"
        class="w-12 h-12 rounded-full border-2 border-vault-border-medium bg-white/90 cursor-pointer transition-all duration-150 flex items-center justify-center text-vault-blue-400 hover:border-vault-border-strong hover:bg-blue-50/90 hover:-translate-y-0.5 hover:shadow-md disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none"
        :disabled="props.disabled"
        @click="pasteFromClipboard"
        title="Paste PIN (or press Ctrl+V / Cmd+V)"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect width="8" height="4" x="8" y="2" rx="1" ry="1"/>
          <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1-2-2h2"/>
        </svg>
      </button>

      <div
        v-for="(slot, index) in props.length"
        :key="index"
        class="w-12 h-12 rounded-xl flex items-center justify-center text-3xl transition-all duration-200"
        :class="{
          'border-2 border-dashed border-vault-border-light bg-blue-50/60 text-vault-text-muted': !pinArray[index],
          'border-2 border-solid border-vault-blue-400/50 bg-blue-100/70 text-vault-text-primary': pinArray[index] && !hasError && !hasSuccess,
          'border-2 border-solid border-vault-danger-400 bg-red-50 text-vault-text-primary': hasError,
          'border-2 border-solid border-vault-success-400 bg-green-50 text-vault-text-primary animate-success-pulse': hasSuccess,
        }"
      >
        {{ pinArray[index] || '•' }}
      </div>

      <button
        type="button"
        class="w-12 h-12 rounded-full border-2 border-vault-border-medium bg-white/90 cursor-pointer transition-all duration-150 flex items-center justify-center text-vault-blue-400 hover:border-vault-border-strong hover:bg-blue-50/90 hover:-translate-y-0.5 hover:shadow-md disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none"
        :disabled="pinArray.length === 0 || props.disabled"
        @click="reset"
        title="Reset PIN"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
          <path d="M21 3v5h-5"/>
          <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
          <path d="M8 16H3v5"/>
        </svg>
      </button>
    </div>

    <!-- Clipboard notification -->
    <transition name="fade">
      <div
        v-if="clipboardError"
        class="max-w-[420px] w-full px-4 py-3 rounded-lg bg-orange-50 border border-orange-200 text-sm text-orange-800"
      >
        {{ clipboardError }}
      </div>
    </transition>

    <div class="grid grid-cols-6 gap-2.5 max-w-[420px] w-full">
      <button
        v-for="emoji in SAFE_EMOJI"
        :key="emoji"
        type="button"
        class="rounded-xl bg-white/90 text-3xl py-2.5 cursor-pointer transition-all duration-150 border border-vault-border-light hover:-translate-y-0.5 hover:shadow-md hover:bg-blue-50/90 disabled:opacity-45 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
        :disabled="isComplete || props.disabled"
        @click="addEmoji(emoji)"
      >
        {{ emoji }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, watch, ref, onMounted, onUnmounted } from 'vue';
import { SAFE_EMOJI } from '@tokenbox/wallet-kit';

const props = defineProps({
  length: {
    type: Number,
    default: 4
  },
  disabled: {
    type: Boolean,
    default: false
  },
  error: {
    type: String,
    default: ''
  }
});

const emit = defineEmits<{
  (event: 'complete', value: string): void;
}>();

const pinArray = reactive<string[]>([]);
const hasError = ref(false);
const hasSuccess = ref(false);
const clipboardError = ref<string>('');
let errorTimeout: ReturnType<typeof setTimeout> | null = null;
let successTimeout: ReturnType<typeof setTimeout> | null = null;

const isComplete = computed(() => pinArray.length === props.length);

watch(() => props.error, (newError) => {
  if (errorTimeout) {
    clearTimeout(errorTimeout);
    errorTimeout = null;
  }

  if (newError) {
    hasError.value = true;
    errorTimeout = setTimeout(() => {
      hasError.value = false;
      errorTimeout = null;
    }, 600);
  } else {
    hasError.value = false;
  }
});

function addEmoji(emoji: string) {
  if (isComplete.value || props.disabled || hasSuccess.value) return;

  // Clear error when user starts entering new PIN
  if (hasError.value) {
    hasError.value = false;
    if (errorTimeout) {
      clearTimeout(errorTimeout);
      errorTimeout = null;
    }
  }

  pinArray.push(emoji);

  if (pinArray.length === props.length) {
    // Show success animation before emitting
    hasSuccess.value = true;

    successTimeout = setTimeout(() => {
      emit('complete', pinArray.join(''));
      hasSuccess.value = false;
      successTimeout = null;
    }, 500);
  }
}

function reset() {
  pinArray.splice(0, pinArray.length);
  hasError.value = false;
  hasSuccess.value = false;

  if (errorTimeout) {
    clearTimeout(errorTimeout);
    errorTimeout = null;
  }

  if (successTimeout) {
    clearTimeout(successTimeout);
    successTimeout = null;
  }
}

// Process pasted text (shared logic)
function processPastedText(text: string) {
  if (!text) {
    clipboardError.value = 'Clipboard is empty';
    setTimeout(() => clipboardError.value = '', 3000);
    return;
  }

  // Extract emojis from pasted text
  const emojis = Array.from(text).filter(char => SAFE_EMOJI.includes(char));

  if (emojis.length === 0) {
    clipboardError.value = 'No valid emojis found';
    setTimeout(() => clipboardError.value = '', 3000);
    return;
  }

  // Clear current PIN and add new emojis
  pinArray.splice(0, pinArray.length);
  const toAdd = emojis.slice(0, props.length);
  pinArray.push(...toAdd);

  // Emit complete if we have enough emojis
  if (pinArray.length === props.length) {
    hasSuccess.value = true;
    successTimeout = setTimeout(() => {
      emit('complete', pinArray.join(''));
      hasSuccess.value = false;
      successTimeout = null;
    }, 500);
  }
}

// Button click handler
async function pasteFromClipboard() {
  if (props.disabled || hasSuccess.value) return;

  clipboardError.value = '';

  try {
    const text = await navigator.clipboard.readText();
    processPastedText(text);
  } catch (err) {
    console.error('Clipboard read failed:', err);
    clipboardError.value = 'Clipboard access denied. Try Ctrl+V (Cmd+V) instead.';
    setTimeout(() => clipboardError.value = '', 3000);
  }
}

// Global paste handler (Ctrl+V / Cmd+V)
function handleGlobalPaste(event: ClipboardEvent) {
  if (props.disabled || hasSuccess.value || isComplete.value) return;

  const text = event.clipboardData?.getData('text');
  if (text) {
    processPastedText(text);
  }
}

// Setup global paste listener
onMounted(() => {
  document.addEventListener('paste', handleGlobalPaste);
});

onUnmounted(() => {
  document.removeEventListener('paste', handleGlobalPaste);
});

watch(
  () => props.length,
  (length) => {
    if (pinArray.length > length) {
      pinArray.splice(length);
    }
  }
);

defineExpose({
  reset
});
</script>

<style scoped>
@keyframes shake {
  0%, 100% {
    transform: translateX(0);
  }
  10%, 30%, 50%, 70%, 90% {
    transform: translateX(-8px);
  }
  20%, 40%, 60%, 80% {
    transform: translateX(8px);
  }
}

@keyframes success-pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

.animate-shake {
  animation: shake 0.6s ease-in-out;
}

.animate-success-pulse {
  animation: success-pulse 0.5s ease;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

@media (max-width: 520px) {
  .grid-cols-6 {
    grid-template-columns: repeat(5, minmax(46px, 1fr));
  }

  .w-12.h-12 {
    width: 40px;
    height: 40px;
  }

  .text-3xl {
    font-size: 24px;
  }
}
</style>
